import type { Ref } from 'vue'
import { BaseText, BaseNumber, BaseSelect, BaseInput } from '~/shared/model'
import _ from 'lodash-es'
import { Container } from '~/shared/container'
export type FilterInputSchema = BaseInput | BaseText | BaseNumber | BaseSelect
export enum FilterOperation {
  Contains = 'contains',
  Is = 'is',
  Is_not = 'is_not',
  Is_between = 'is_between',
  gt = 'gt',
  lt = 'lt',
  gte = 'gte',
  lte = 'lte',
  eq = 'eq',
}

export type FilterSchema = FilterInputSchema & {
  $el?: string
  target?: string
  operation?: string
  defaultStateValue?: any
  children?: string[]
}
export type FilterInput = {
  name: string
  operation: FilterOperation
  target?: string
  values: string[]
}

export const FilterMapper = {
  INT: (v): number => +v,
  STR: (v): string => v.toString(),
  TOGGLE: (v): boolean => v === true || v === 'true' || undefined,
}

export function useFiltersShare(state: Ref<any>) {
  const route = useRoute()

  const { origin } = window.location

  const shareLink = computed(() => {
    const parsedState = Object.keys(state.value || state || {}).reduce(
      (acc, key) => {
        if (state.value[key]) acc[key] = state.value[key]
        return acc
      },
      {},
    )
    const queryParams = new URLSearchParams({
      ...route.query,
      ...parsedState,
    }).toString()
    return `${origin}${route.path}${queryParams ? '?' + queryParams : ''}`
  })

  return shareLink
}

const arrayToObj = (arr: any[]) =>
  arr.reduce((acc, item) => ({ ...acc, ...item }), {})

export function useFiltersParams(
  state: Ref<any>,
  parsedSchema: Record<string, FilterSchema>,
) {
  const params = computed(() => {
    return arrayToObj(
      Object.keys(state.value).map((key) => {
        const filter = parsedSchema[key]
        const value = state.value[key]
        const operation = filter?.operation
          ? filter.operation
          : FilterOperation.Contains

        return {
          [key]: {
            operation,
            value,
          },
        }
      }),
    )
  })

  return params
}

export function useFilterQueries(
  state: Ref<any>,
  parsedSchema: Record<string, FilterSchema>,
) {
  const queries = computed<FilterInput[]>(() => {
    return Object.keys(state.value)
      .map((key) => {
        const filter = parsedSchema[key]
        const value = state.value[key]
        const operation = filter?.operation
          ? filter.operation
          : FilterOperation.Contains

        return {
          name: key,
          target: filter.target,
          operation: (operation as FilterOperation) || FilterOperation.Contains,
          values: ((Array.isArray(value) ? value : [value]) || [])
            .filter((v) => !!v)
            .map((v) => `${v}`),
        }
      })
      .filter((f) => f.values.length)
  })

  return queries
}

export function useFiltersState<T = any>(
  ...schemas: (FilterSchema[] | Container)[]
) {
  const multi = new Multischema(schemas)
  const parsedSchema = multi.getParsedState() //schemasToObj(schemas);
  const initialState = multi.getInitialState() //getInitialState(parsedSchema) as any;
  const { queryState } = useQueryFilters(parsedSchema)
  
  const state: Ref<T> = ref({ ...initialState, ...queryState.value })
  console.log("[Filters] Initial filter state", jsonClone(state.value))
  const schema = multi.getFormkit() as FilterSchema[] // multischemaToSchema(schemas) as FilterSchema[];
  // could be moved in future, but right now I don't believe it covers all use cases.
  const clearState = () => {
    const newState = Object.fromEntries(
        Object.keys(state.value).map((key) => {
          if (Array.isArray(state.value[key])) {
            return [key, initialState[key] || []]
          } else {
            return [key, initialState[key] || undefined]
          }
        }),
    )

    state.value = newState as any
  }
  // const clearState = () => Object.keys(state.value).reduce((acc, key) => ({ ...acc, [key]: undefined }), {})
  const pagination = useRoutePagination()
  watch(state, () => {
    // console.log("State changed to ", JSON.parse(JSON.stringify(state.value)))
    if (pagination.value.page > 1)
      pagination.value = { ...pagination.value, page: 1 }
  })
  
  return {
    state,
    schema,
    pagination,
    parsedFiltersSchema: parsedSchema,
    clearState,
    get<V>(name: string, map?: (v: any) => V) {
      const v = _.get(state.value, name)
      const value = map && v ? map(v) : v

      return {
        asValue: (): V => value,
        asNotNullValue: (): V | undefined =>
          value === null ? undefined : value,
        asWrapper: <K>(
          map: (v: V) => K,
          { allowNull }: { allowNull?: boolean } = {},
        ) => (value || (allowNull && value === null) ? map(value) : undefined),
        asEquals: ({ allowNull }: { allowNull?: boolean } = {}):
          | { equals: V }
          | undefined =>
          value || (allowNull && value === null)
            ? { equals: value }
            : undefined,
        asContains: (): { contains: V } | undefined =>
          value ? { contains: value } : undefined,
        asNumberIn: (): { in: number[] } | undefined =>
          value && (!Array.isArray(value) || value.length)
            ? { in: handlePossibleArrayValue(value) }
            : undefined,
        asToggleValue: <B = boolean>(trueValue?: B): B | undefined =>
          value === true || value === 'true'
            ? trueValue || (true as B)
            : undefined,
        asNested: (key: string): { [key: string]: V } =>
          value ? { [key]: value } : undefined,
        asTrueFalseValue: (): boolean | undefined =>
          value === 'true' || value === 'false' ? value === 'true' : undefined,
        hasValue: (): boolean  => {
          if (typeof value === 'number') {
            return true
          }
          if (typeof value === 'string') {
            return value.trim().length > 0
          }
          if (Array.isArray(value) && value.length) {
            return true
          }

          return !!value
        }
      }
    },
  }
}

export function usePersistedSessionFilter<T>(state: Ref<T>) {
  const route = useRoute()
  const store = useStore()
  try {
    const filters = store.getPageFilters(route.path)
    
    // We don't want to fill values if they were already filled from query
    // Check if there are any unusual query keys
    const hasQuery = Object.keys(route.query).some(key => !['page', 'perPage', 'sort', 'orderBy'].includes(key))

    if (filters && !hasQuery) {
      state.value = filters
    }
  } catch (e) {
    console.error('Failed to set seesion filters', e)
  }

  watch(state, () => {
    try {
      store.addPageFilters({ path: route.path, filters: state.value })
    } catch (e) {
      console.error(e)
    }
  })

  return state
}

export function usePersistedSessionSort<T>(state: Ref<T>) {
  const route = useRoute()
  const store = useStore()
  try {
    const sorts = store.getPageSorts(route.path)
    if (sorts) {
      state.value = sorts
    }
  } catch (e) {
    console.error(e)
  }

  watch(state, () => {
    try {
      store.addPageSorts({ path: route.path, sorts: state.value })
    } catch (e) {
      console.error(e)
    }
  })

  return state
}

class Multischema {
  private containers: Container[]
  private parsedState: Record<string, FilterSchema>
  private initialState: any

  constructor(multichema: (FilterSchema[] | Container)[]) {
    this.containers = multichema
      .map((children) => {
        if (Array.isArray(children)) {
          return new Container({
            children,
          })
        }
        return children
      })
      .filter((c) => !!c)
    this.parsedState = this.asParsedState()
    this.initialState = this.asInitialState()
  }

  private asParsedState() {
    return this.containers.reduce((acc, c) => {
      const inputs = c.inputs()
      const obj = inputs.reduce(
        (acc2, i) => ({ ...acc2, [i.name]: i }),
        {} as any,
      )
      return { ...acc, ...obj }
    }, {} as Record<string, FilterSchema>)
  }

  private asInitialState() {
    const parsed = this.parsedState
    const initial = Object.keys(parsed).reduce((acc, key) => {
      acc[key] = parsed[key]?.defaultStateValue || undefined
      return acc
    }, {})
    return initial
  }

  getParsedState() {
    return this.parsedState
  }

  getInitialState() {
    return this.initialState
  }

  getFormkit(): (FilterSchema | { $el: string })[] {
    return this.containers.map((c) => c.getFormkit())
  }
}
